import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Modal, Text } from '@fragments';
import { IconPlus, IconSearch, IconLayoutGrid, IconFilterOff } from '@tabler/icons-react';
import { studioApi, idempotencyKey } from '../../../../data/studioApi';
import { CHANNELS, CHANNEL_ICONS, OBJECTIVES } from '../../../../enums/automations';
import { automationEditPath } from '../../../../enums/routes';
import TemplateCard from '../TemplateCard';
import PageState from '../PageState';
import './styles.scss';

const ALL = { kind: 'all', value: null };

// "Nova automação": the catalog (GET /automations/templates), filtered by goal and by trigger, or
// a blank start. Either way POST /automations creates the draft and the editor opens on it.
export default function TemplatesDialog({ studio: s, onClose }) {
  const { t } = useTranslation('studioAutomations');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState(ALL);
  const [templates, setTemplates] = useState(null);
  const [failed, setFailed] = useState(false);
  const [creating, setCreating] = useState(null);
  const profileId = s.profile.id;
  const reads = useRef(0);

  useEffect(() => {
    const read = ++reads.current;
    const timer = setTimeout(async () => {
      try {
        const page = await studioApi.automationTemplates({ profile_id: profileId, q: query.trim() || undefined, objective: filter.kind === 'objective' ? filter.value : undefined, channel: filter.kind === 'channel' ? filter.value : undefined });
        if (read === reads.current) { setTemplates(page.data); setFailed(false); }
      } catch { if (read === reads.current) setFailed(true); }
    }, query ? 240 : 0);
    return () => clearTimeout(timer);
  }, [profileId, query, filter]);

  const create = async template => {
    setCreating(template?.key ?? 'blank');
    try {
      const automation = await studioApi.createAutomation({ profile_id: profileId, ...(template ? { template_key: template.key } : {}) }, idempotencyKey('automation'));
      s.automations.refresh();
      onClose();
      s.navigate(automationEditPath(automation.id));
    } catch (failure) {
      setCreating(null);
      s.act(() => { throw failure; });
    }
  };

  const groups = useMemo(() => [
    { key: 'recommended', title: t('templatesRecommended'), items: (templates || []).filter(item => item.recommended) },
    { key: 'more', title: t('templatesMore'), items: (templates || []).filter(item => !item.recommended) },
  ].filter(group => group.items.length), [templates, t]);

  const rail = (kind, value, label, icon = null) => {
    const on = filter.kind === kind && filter.value === value;
    return <Button key={`${kind}-${value}`} fullWidth variant="rounded" discreet={!on} color="overlay" className={`studio-automation-templates__filter${on ? ' is-selected' : ''}`} startIcon={icon} aria-pressed={on} preventLoading onClick={() => setFilter({ kind, value })}>
      <Text.span contrast={on ? 9 : 7}>{label}</Text.span>
    </Button>;
  };

  return <Modal open onClose={onClose} title={t('templatesTitle')} subtitle={t('templatesSubtitle')} size={1000} closeLabel={t('studio:close')} className="studio-automation-templates">
    <div className="studio-automation-templates__tools">
      <Input.Text value={query} onInput={setQuery} placeholder={t('templatesSearch')} aria-label={t('templatesSearch')} start={<IconSearch size={16} />} fullWidth autoFocus />
      <Button variant="rounded" color="overlay" startIcon={<IconPlus />} loading={creating === 'blank'} disabled={!!creating} preventLoading onClick={() => create(null)}>{t('templatesBlank')}</Button>
    </div>
    <div className="studio-automation-templates__layout">
      <nav className="studio-automation-templates__rail" aria-label={t('templatesFilters')}>
        {rail('all', null, t('templatesAll'), <IconLayoutGrid />)}
        <Text.caption contrast={6}>{t('templatesByObjective')}</Text.caption>
        {OBJECTIVES.map(value => rail('objective', value, t(`objective_${value}`)))}
        <Text.caption contrast={6}>{t('templatesByTrigger')}</Text.caption>
        {CHANNELS.map(value => { const Glyph = CHANNEL_ICONS[value]; return rail('channel', value, t(`channel_${value}`), <Glyph />); })}
      </nav>
      <div className="studio-automation-templates__results" aria-busy={!templates}>
        {failed ? <PageState compact text={t('loadError')} />
          : !templates ? <div className="studio-automation-templates__grid">{[0, 1, 2].map(key => <div key={key} className="studio-automation-templates__skeleton"><Text.label loading>{t('templatesTitle')}</Text.label><Text.span loading>{t('templatesSubtitle')}</Text.span></div>)}</div>
          : groups.length ? groups.map(group => <section key={group.key} className="studio-automation-templates__group">
              <Text.label contrast={8}>{group.title}</Text.label>
              <div className="studio-automation-templates__grid">
                {group.items.map((template, index) => <TemplateCard key={template.key} studio={s} template={template} index={index} busy={creating === template.key} onPick={create} />)}
              </div>
            </section>)
          : <PageState compact icon={<IconFilterOff />} text={t('templatesEmpty')} />}
      </div>
    </div>
  </Modal>;
}
